import React, { useState, useEffect } from 'react';

const AITradeConfigSummaryModal = ({ onClose, authenticatedFetch, user }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!authenticatedFetch) {
      setError('인증 함수가 제공되지 않았습니다.');
      setLoading(false);
      return;
    }
    fetchConfig();
  }, [authenticatedFetch]);

  const fetchConfig = async () => {
    if (!authenticatedFetch) {
      setError('인증 함수가 제공되지 않았습니다.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await authenticatedFetch('/api/ai-trade/config', {
        timeout: 120000 // 120초 - AI 설정/분석 관련
      });
      if (response.ok) {
        const data = await response.json();
        setConfig(data.config);
      } else if (response.status === 401) {
        setError('인증이 필요합니다. 로그인 후 다시 시도해주세요.');
      } else if (response.status === 403) {
        setError('접근 권한이 없습니다.');
      } else if (response.status === 404) {
        setError('설정을 찾을 수 없습니다.');
      } else {
        setError(`설정을 불러오는데 실패했습니다. (상태 코드: ${response.status})`);
      }
    } catch (err) {
      console.error('설정 조회 오류:', err);
      // 401 에러는 authenticatedFetch에서 throw됨
      if (err.message === 'Authentication required' || err.message?.includes('401')) {
        setError('인증이 필요합니다. 로그인 후 다시 시도해주세요.');
      } else if (err.message?.includes('Rate limit')) {
        setError('요청이 너무 많습니다. 잠시 후 다시 시도해주세요.');
      } else if (err.name === 'AbortError' || err.message?.includes('timeout')) {
        setError('요청 시간이 초과되었습니다. 네트워크 연결을 확인하고 다시 시도해주세요.');
      } else {
        setError(`설정을 불러오는 중 오류가 발생했습니다: ${err.message || '알 수 없는 오류'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // 설정값 가져오기 헬퍼 함수
  const getValue = (path, defaultValue = null) => {
    if (!config) return defaultValue;
    
    // metadata에서도 확인 (하위 호환성)
    const metadata = config.metadata instanceof Map 
      ? Object.fromEntries(config.metadata) 
      : config.metadata;
    
    const value = path.split('.').reduce((obj, key) => {
      if (obj && typeof obj === 'object') {
        return obj[key];
      }
      return undefined;
    }, config);
    
    if (value !== undefined && value !== null) {
      return value;
    }
    
    // metadata에서도 확인
    const metadataValue = path.split('.').reduce((obj, key) => {
      if (obj && typeof obj === 'object') {
        return obj[key];
      }
      return undefined;
    }, metadata);
    
    return metadataValue !== undefined && metadataValue !== null ? metadataValue : defaultValue;
  };

  // 분석 깊이 한글 변환
  const getAnalysisDepthText = (depth) => {
    const map = {
      'LOW': '낮음',
      'MEDIUM': '보통',
      'HIGH': '높음'
    };
    return map[depth] || depth;
  };

  // 투자 성향 한글 변환
  const getRiskToleranceText = (risk) => {
    const map = {
      'conservative': '보수적',
      'moderate': '중립적',
      'aggressive': '공격적'
    };
    return map[risk] || (risk || '미설정');
  };

  // 투자 목표 한글 변환
  const getPrimaryGoalText = (goal) => {
    const map = {
      'capital_growth': '자본 성장',
      'income': '수익 창출',
      'preservation': '자본 보존',
      'speculation': '투기'
    };
    return map[goal] || (goal || '미설정');
  };

  // 투자 기간 한글 변환
  const getTimeHorizonText = (horizon) => {
    const map = {
      'short_term': '단기 (1년 이하)',
      'medium_term': '중기 (1-5년)',
      'long_term': '장기 (5년 이상)'
    };
    return map[horizon] || (horizon || '미설정');
  };

  // 시가총액 한글 변환
  const getMarketCapText = (cap) => {
    const map = {
      'large': '대형주',
      'mid': '중형주',
      'small': '소형주',
      'all': '전체'
    };
    return map[cap] || (cap || '미설정');
  };

  // 갱신 주기 설명
  const getUpdateFrequencyText = (weeks) => {
    if (weeks === 1) return '매주';
    if (weeks === 2) return '격주 (2주마다)';
    if (weeks === 4) return '매월';
    return `${weeks}주마다`;
  };

  // 다음 갱신 시간 계산
  const getNextUpdateTime = () => {
    if (config?.next_full_update_time) {
      return new Date(config.next_full_update_time).toLocaleString('ko-KR');
    }
    if (config?.last_full_analysis_time) {
      const lastTime = new Date(config.last_full_analysis_time);
      const updateWeeks = getValue('analysis_config.update_frequency_weeks', 1);
      const nextTime = new Date(lastTime);
      nextTime.setDate(nextTime.getDate() + (updateWeeks * 7));
      return nextTime.toLocaleString('ko-KR');
    }
    return '미설정';
  };

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 10000
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '30px',
          borderRadius: '10px',
          minWidth: '300px',
          textAlign: 'center'
        }}>
          <div>설정을 불러오는 중...</div>
        </div>
      </div>
    );
  }

  if (error) {
    const isAuthError = error.includes('인증') || error.includes('로그인');
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 10000
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '30px',
          borderRadius: '10px',
          minWidth: '300px',
          maxWidth: '600px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
        }}>
          <div style={{
            fontSize: '48px',
            textAlign: 'center',
            marginBottom: '15px'
          }}>
            {isAuthError ? '🔒' : '⚠️'}
          </div>
          <h2 style={{ 
            marginTop: 0, 
            textAlign: 'center',
            color: isAuthError ? '#f44336' : '#ff9800'
          }}>
            {isAuthError ? '인증 필요' : '오류 발생'}
          </h2>
          <p style={{
            textAlign: 'center',
            color: '#666',
            lineHeight: '1.6',
            marginBottom: '20px'
          }}>
            {error}
          </p>
          {isAuthError && (
            <p style={{
              fontSize: '13px',
              color: '#999',
              textAlign: 'center',
              marginBottom: '20px',
              fontStyle: 'italic'
            }}>
              로그인 후 설정 요약을 확인할 수 있습니다.
            </p>
          )}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '10px'
          }}>
            {isAuthError && (
              <button
                onClick={() => {
                  onClose();
                  // 페이지 새로고침하여 로그인 화면 표시
                  window.location.reload();
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                로그인 페이지로
              </button>
            )}
            <button
              onClick={onClose}
              style={{
                padding: '10px 20px',
                backgroundColor: isAuthError ? '#667eea' : '#f44336',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              닫기
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isActive = getValue('is_active', false);
  const hasApiKey = getValue('google_api_key') && getValue('google_api_key').length > 0;
  const isPaperTrading = getValue('is_paper_trading', true);
  const executeImmediately = getValue('execute_immediately', false);

  // 자동 매수 설정
  const autoBuyEnabled = getValue('auto_buy.enabled', false);
  const autoBuyConfidence = getValue('auto_buy.confidence_threshold', 70);
  const autoBuyQuantity = getValue('auto_buy.quantity_percent', 10);
  const autoBuyMaxDaily = getValue('auto_buy.max_daily_purchases', 5);
  const autoBuyMaxHolding = getValue('auto_buy.max_holding_percent', 20);
  const autoBuyMaxHoldings = getValue('auto_buy.max_holdings_count', 20);
  const autoBuyMaxPriceChange = getValue('auto_buy.max_price_change_percent', 10);
  const autoBuyAdjustPrice = getValue('auto_buy.adjust_price_on_volatility', true);
  const maxBalanceUsage = getValue('auto_buy.balance_limits.max_balance_usage_percent', 80);
  const reservedBalance = getValue('auto_buy.balance_limits.reserved_balance', 0);
  const designatedBalance = getValue('auto_buy.balance_limits.designated_cash_balance', 0);
  const minInvestment = getValue('auto_buy.min_investment_amount', 100000);

  // 자동 매도 설정
  const autoSellEnabled = getValue('auto_sell.enabled', false);
  const autoSellConfidence = getValue('auto_sell.confidence_threshold', 70);
  const autoSellQuantity = getValue('auto_sell.quantity_percent', 50);
  const autoSellMaxPriceChange = getValue('auto_sell.max_price_change_percent', 10);
  const autoSellAdjustPrice = getValue('auto_sell.adjust_price_on_volatility', true);
  const forceSellOnLoss = getValue('auto_sell.force_sell_on_loss_10_percent', false);
  const lossSellQuantity = getValue('auto_sell.loss_10_percent_sell_quantity_percent', 100);

  // 분석 설정
  const analysisPeriod = getValue('analysis_config.analysis_period_days', 365);
  const analysisDepth = getValue('analysis_config.analysis_depth', 'HIGH');
  const includeWebSearch = getValue('analysis_config.include_web_search', true);
  const includeCompanyData = getValue('analysis_config.include_company_data', true);
  const updateFrequency = getValue('analysis_config.update_frequency_weeks', 1);
  const useGemini3 = getValue('analysis_config.use_gemini_3_flash', false);

  // 사용자 프로필
  const riskTolerance = getValue('user_profile.risk_tolerance', '');
  const monthlyIncome = getValue('user_profile.financial_status.monthly_income', 0);
  const investableAssets = getValue('user_profile.financial_status.investable_assets', 0);
  const experienceYears = getValue('user_profile.financial_status.investment_experience_years', 0);
  const primaryGoal = getValue('user_profile.investment_goals.primary_goal', '');
  const timeHorizon = getValue('user_profile.investment_goals.time_horizon', '');
  const targetReturn = getValue('user_profile.investment_goals.target_return_percent', 0);
  const preferredSectors = getValue('user_profile.preferences.preferred_sectors', []);
  const excludedSectors = getValue('user_profile.preferences.excluded_sectors', []);
  const preferredMarketCap = getValue('user_profile.preferences.preferred_market_cap', '');
  const maxPositionSize = getValue('user_profile.preferences.max_position_size_percent', 20);

  // 종목 수
  const symbolCount = config?.symbol_configs?.length || 0;

  // User.trading 금지종목 (API에서 내려주는 값)
  const userTrading = user?.trading || {};
  const blockedSymbols = Array.isArray(userTrading.blocked_symbols) ? userTrading.blocked_symbols : [];
  const blockedBuySymbols = Array.isArray(userTrading.blocked_buy_symbols) ? userTrading.blocked_buy_symbols : [];
  const blockedSellSymbols = Array.isArray(userTrading.blocked_sell_symbols) ? userTrading.blocked_sell_symbols : [];

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 10000,
      overflow: 'auto',
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '10px',
        padding: '30px',
        maxWidth: '800px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '25px',
          borderBottom: '2px solid #e0e0e0',
          paddingBottom: '15px'
        }}>
          <h2 style={{ margin: 0, color: '#333' }}>📋 AI 거래 설정 요약</h2>
          <button
            onClick={onClose}
            style={{
              backgroundColor: '#f44336',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              width: '30px',
              height: '30px',
              cursor: 'pointer',
              fontSize: '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>

        {/* 기본 상태 */}
        <div style={{
          marginBottom: '25px',
          padding: '20px',
          backgroundColor: isActive ? '#d4edda' : '#f8f9fa',
          borderRadius: '8px',
          border: `2px solid ${isActive ? '#28a745' : '#dee2e6'}`
        }}>
          <h3 style={{ marginTop: 0, marginBottom: '15px', color: '#333' }}>
            {isActive ? '✅ AI 자동거래 활성화' : '⏸️ AI 자동거래 비활성화'}
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '14px' }}>
            <div>
              <strong>거래 모드:</strong> {isPaperTrading ? '📝 모의투자' : '💰 실전투자'}
            </div>
            <div>
              <strong>API 키:</strong> {hasApiKey ? '✅ 설정됨' : '⚠️ 미설정'}
            </div>
            <div>
              <strong>즉시 실행:</strong> {executeImmediately ? '✅ 활성화' : '⏸️ 비활성화'}
            </div>
            <div>
              <strong>분석 종목 수:</strong> {symbolCount}개
            </div>
          </div>
        </div>

        {/* 매수 기준 */}
        <div style={{
          marginBottom: '25px',
          padding: '20px',
          backgroundColor: '#e7f3ff',
          borderRadius: '8px',
          border: '1px solid #b3d9ff'
        }}>
          <h3 style={{ marginTop: 0, marginBottom: '15px', color: '#0056b3' }}>
            📈 매수 기준
          </h3>
          {autoBuyEnabled ? (
            <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
              <div><strong>자동 매수:</strong> ✅ 활성화</div>
              <div><strong>확신도 임계값:</strong> {autoBuyConfidence}% 이상</div>
              <div><strong>매수 수량:</strong> 사용 가능 잔고의 {autoBuyQuantity}%</div>
              <div><strong>일일 최대 매수:</strong> {autoBuyMaxDaily}회</div>
              <div><strong>종목당 최대 보유:</strong> 포트폴리오의 {autoBuyMaxHolding}%</div>
              <div><strong>최대 보유 종목 수:</strong> {autoBuyMaxHoldings}개</div>
              <div><strong>가격 변동률 제한:</strong> {autoBuyMaxPriceChange}% (초과 시 {autoBuyAdjustPrice ? '현재가로 조정' : '주문 취소'})</div>
              <div><strong>최소거래비용:</strong> {minInvestment.toLocaleString()}원</div>
              <div style={{ marginTop: '10px', padding: '10px', backgroundColor: 'white', borderRadius: '5px' }}>
                <strong>잔고 제한:</strong>
                <div style={{ marginLeft: '10px', marginTop: '5px' }}>
                  <div>• 최대 잔고 사용률: {maxBalanceUsage}%</div>
                  {reservedBalance > 0 && <div>• 예약 잔고: {reservedBalance.toLocaleString()}원</div>}
                  {designatedBalance > 0 && <div>• 지정 현금 잔고: {designatedBalance.toLocaleString()}원</div>}
                </div>
              </div>
            </div>
          ) : (
            <div style={{ fontSize: '14px', color: '#666' }}>
              ⏸️ 자동 매수가 비활성화되어 있습니다.
            </div>
          )}
        </div>

        {/* 매도 기준 */}
        <div style={{
          marginBottom: '25px',
          padding: '20px',
          backgroundColor: '#fff3cd',
          borderRadius: '8px',
          border: '1px solid #ffc107'
        }}>
          <h3 style={{ marginTop: 0, marginBottom: '15px', color: '#856404' }}>
            📉 매도 기준
          </h3>
          {autoSellEnabled ? (
            <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
              <div><strong>자동 매도:</strong> ✅ 활성화</div>
              <div><strong>확신도 임계값:</strong> {autoSellConfidence}% 이상</div>
              <div><strong>매도 수량:</strong> 보유 수량의 {autoSellQuantity}%</div>
              <div><strong>가격 변동률 제한:</strong> {autoSellMaxPriceChange}% (초과 시 {autoSellAdjustPrice ? '현재가로 조정' : '주문 취소'})</div>
              {forceSellOnLoss && (
                <div style={{ marginTop: '10px', padding: '10px', backgroundColor: 'white', borderRadius: '5px', border: '1px solid #dc3545' }}>
                  <strong style={{ color: '#dc3545' }}>⚠️ 손실 10% 초과 시 강제 매도:</strong> 활성화
                  <div style={{ marginLeft: '10px', marginTop: '5px' }}>
                    • 매도 수량: {lossSellQuantity}%
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div style={{ fontSize: '14px', color: '#666' }}>
              ⏸️ 자동 매도가 비활성화되어 있습니다.
            </div>
          )}
        </div>

        {/* 분석 설정 */}
        <div style={{
          marginBottom: '25px',
          padding: '20px',
          backgroundColor: '#f0f0f0',
          borderRadius: '8px',
          border: '1px solid #ccc'
        }}>
          <h3 style={{ marginTop: 0, marginBottom: '15px', color: '#333' }}>
            🔍 분석 설정
          </h3>
          <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
            <div><strong>분석 기간:</strong> 최근 {analysisPeriod}일 ({Math.floor(analysisPeriod / 365)}년)</div>
            <div><strong>분석 깊이:</strong> {getAnalysisDepthText(analysisDepth)}</div>
            <div><strong>웹 검색 포함:</strong> {includeWebSearch ? '✅ 포함' : '❌ 미포함'}</div>
            <div><strong>기업 자료 포함:</strong> {includeCompanyData ? '✅ 포함' : '❌ 미포함'}</div>
            <div><strong>AI 모델:</strong> {useGemini3 ? 'Gemini 3 Flash' : 'Gemini 2.5 Flash'}</div>
            <div><strong>갱신 주기:</strong> {getUpdateFrequencyText(updateFrequency)}</div>
            <div><strong>다음 갱신 예정:</strong> {getNextUpdateTime()}</div>
          </div>
        </div>

        {/* 스케줄 정보 */}
        <div style={{
          marginBottom: '25px',
          padding: '20px',
          backgroundColor: '#e8f5e9',
          borderRadius: '8px',
          border: '1px solid #81c784'
        }}>
          <h3 style={{ marginTop: 0, marginBottom: '15px', color: '#2e7d32' }}>
            📅 스케줄 정보
          </h3>
          <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
            {config?.last_full_analysis_time ? (
              <div><strong>마지막 분석:</strong> {new Date(config.last_full_analysis_time).toLocaleString('ko-KR')}</div>
            ) : (
              <div><strong>마지막 분석:</strong> 아직 분석 이력이 없습니다.</div>
            )}
            <div><strong>다음 갱신:</strong> {getNextUpdateTime()}</div>
            <div style={{ marginTop: '10px', padding: '10px', backgroundColor: 'white', borderRadius: '5px' }}>
              <strong>갱신 방식:</strong>
              <div style={{ marginLeft: '10px', marginTop: '5px' }}>
                • {getUpdateFrequencyText(updateFrequency)} 자동으로 AI 분석 수행
                <br />
                • 분석 완료 후 매수/매도 추천 생성
                {executeImmediately && (
                  <>
                    <br />
                    • 즉시 실행 활성화: 추천 즉시 주문 생성
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* 금지종목 (User.trading) */}
        <div style={{
            marginBottom: '25px',
            padding: '20px',
            backgroundColor: '#fff8e1',
            borderRadius: '8px',
            border: '1px solid #ffc107'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '15px', color: '#f57c00' }}>
              🚫 금지종목 (AI 자동거래 제외)
            </h3>
            <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
              프로필에서 수정할 수 있습니다.
            </p>
            <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
              <div><strong>거래 금지 (매수·매도 모두):</strong> {blockedSymbols.length > 0 ? blockedSymbols.join(', ') : '없음'}</div>
              <div><strong>매수 금지:</strong> {blockedBuySymbols.length > 0 ? blockedBuySymbols.join(', ') : '없음'}</div>
              <div><strong>매도 금지:</strong> {blockedSellSymbols.length > 0 ? blockedSellSymbols.join(', ') : '없음'}</div>
            </div>
          </div>

        {/* 사용자 프로필 (간략) */}
        {(riskTolerance || primaryGoal || preferredSectors.length > 0) && (
          <div style={{
            marginBottom: '25px',
            padding: '20px',
            backgroundColor: '#f3e5f5',
            borderRadius: '8px',
            border: '1px solid #ba68c8'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '15px', color: '#6a1b9a' }}>
              👤 투자 프로필 (요약)
            </h3>
            <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
              {riskTolerance && <div><strong>투자 성향:</strong> {getRiskToleranceText(riskTolerance)}</div>}
              {primaryGoal && <div><strong>투자 목표:</strong> {getPrimaryGoalText(primaryGoal)}</div>}
              {timeHorizon && <div><strong>투자 기간:</strong> {getTimeHorizonText(timeHorizon)}</div>}
              {targetReturn > 0 && <div><strong>목표 수익률:</strong> {targetReturn}%</div>}
              {preferredSectors.length > 0 && (
                <div><strong>선호 업종:</strong> {preferredSectors.join(', ')}</div>
              )}
              {excludedSectors.length > 0 && (
                <div><strong>제외 업종:</strong> {excludedSectors.join(', ')}</div>
              )}
              {preferredMarketCap && <div><strong>선호 시가총액:</strong> {getMarketCapText(preferredMarketCap)}</div>}
              <div><strong>종목당 최대 비중:</strong> {maxPositionSize}%</div>
            </div>
          </div>
        )}

        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: '20px',
          paddingTop: '20px',
          borderTop: '1px solid #e0e0e0'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '12px 30px',
              backgroundColor: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '600'
            }}
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
};

export default AITradeConfigSummaryModal;

